"use client";

import * as React from "react";
import { Button, TextInput, PasswordInput, LoadingOverlay, Text, Center, Title, Container, Stack, Tabs, Box, useMantineColorScheme } from "@mantine/core";
import { MFAForm } from "./mfa-form";
import { MFASetupForm } from "./mfa-setup-form";
import { useAuth } from "../../hooks/use-auth";
import type { LoginRequest, LdapRequest, AuthResponse } from "../../lib/types";
import logo from "../../assets/logo.png";

interface LoginFormProps {
  onSuccess?: () => void;
  title?: string;
  onLogin: (
    credentials: LoginRequest | LdapRequest,
    isLdap: boolean
  ) => Promise<AuthResponse>;
  app?: "web" | "admin";
  hideLogo?: boolean;
  fullHeight?: boolean;
  logoHiddenFrom?: "sm" | "md" | "lg" | "xl";
}

export function LoginForm({ onSuccess, onLogin, title, app, hideLogo, fullHeight = true, logoHiddenFrom }: LoginFormProps) {
  const { colorScheme } = useMantineColorScheme();
  const { mfaRequired, mfaSetupRequired, mfaToken, verifyMfa, logout, setSessionToken } = useAuth();

  const [authMethod, setAuthMethod] = React.useState<string>("password");
  const [email, setEmail] = React.useState("");
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [companyAlias, setCompanyAlias] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (authMethod === "password") {
        if (!email || !password || !companyAlias) {
          setError("Email, password, and company alias are required");
          setIsLoading(false);
          return;
        }
        const credentials: LoginRequest = {
          email,
          password,
          companyAlias,
          app,
        };
        const response = await onLogin(credentials, false);
        if (!response.mfaRequired && !response.mfaSetupRequired) {
          onSuccess?.();
        }
        setIsLoading(false);
      } else {
        if (!username || !password || !companyAlias) {
          setError("Username, password, and company alias are required");
          setIsLoading(false);
          return;
        }
        const credentials: LdapRequest = {
          username,
          password,
          companyAlias,
          app,
        };
        const response = await onLogin(credentials, true);
        if (!response.mfaRequired && !response.mfaSetupRequired) {
          onSuccess?.();
        }
        setIsLoading(false);
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Authentication failed. Please check your credentials."
      );
      setIsLoading(false);
    }
  };

  const handleMfaVerify = async (code: string) => {
    await verifyMfa(code);
    onSuccess?.();
  };

  if (mfaRequired) {
    return (
      <Container size="xs" py="xl">
        <Center style={{ minHeight: "100vh" }}>
          <MFAForm onVerify={handleMfaVerify} onCancel={logout} />
        </Center>
      </Container>
    );
  }

  if (mfaSetupRequired && mfaToken) {
    return (
      <Container size="xs" py="xl">
        <Center style={{ minHeight: "100vh" }}>
          <MFASetupForm
            mfaToken={mfaToken}
            onSuccess={async (token) => {
              await setSessionToken(token);
              onSuccess?.();
            }}
            onCancel={logout}
          />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xs" py={fullHeight ? "xl" : 0}>
      <Center style={{ minHeight: fullHeight ? "100vh" : "auto" }}>
        <Stack w="100%" gap="md">
          {!hideLogo && (
            <Box
              p={30}
              hiddenFrom={logoHiddenFrom}
              style={{
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
                borderRadius: 'var(--mantine-radius-md)',
                border: '1px solid var(--modal-border)',
                backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
                boxShadow: 'var(--mantine-shadow-md)'
              }}
            >
              <Center>
                <Stack gap="xs" align="center">
                  <img
                    src={typeof logo === 'string' ? logo : (logo as any).src}
                    alt="Logo"
                    className="login-logo"
                    style={{
                      height: 48,
                      width: "auto",
                    }}
                  />
                  <style dangerouslySetInnerHTML={{
                    __html: `
                    .login-logo {
                      mix-blend-mode: multiply;
                    }
                    [data-mantine-color-scheme="dark"] .login-logo {
                      filter: invert(1) hue-rotate(180deg) brightness(1.2);
                      mix-blend-mode: screen;
                    }
                    .no-scrollbar {
                      -ms-overflow-style: none;
                      scrollbar-width: none;
                    }
                    .no-scrollbar::-webkit-scrollbar {
                      display: none;
                    }
                  `}} />
                  {title && <Title order={4}>{title}</Title>}
                </Stack>
              </Center>
            </Box>
          )}

          <Box
            p={30}
            style={{
              backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.01)' : 'rgba(255, 255, 255, 0.5)',
              borderRadius: 'var(--mantine-radius-md)',
              border: '1px solid var(--modal-border)',
              backdropFilter: colorScheme === 'light' ? 'blur(10px)' : undefined,
              boxShadow: 'var(--mantine-shadow-md)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box
              mx="-var(--mantine-spacing-xl)"
              mt="-var(--mantine-spacing-xl)"
              mb="md"
              py="xs"
              px="md"
              style={{
                borderBottom: '1px solid var(--modal-border)',
                backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.05)'
              }}
            >
              <Text fw={600}>Login & secure access</Text>
              <Text size="xs" c={colorScheme === 'dark' ? 'gray.5' : 'gray.7'}>Authenticate with your tenant credentials to unlock analytics workspaces.</Text>
            </Box>

            <form onSubmit={handleSubmit}>
              <Stack gap="md" mt="md">
                <LoadingOverlay visible={isLoading} />
                <LoadingOverlay visible={isLoading} />

                <Tabs value={authMethod} onChange={(val) => setAuthMethod(val || "password")}>
                  <Tabs.List className="no-scrollbar" style={{ flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}>
                    <Tabs.Tab value="password" style={{ whiteSpace: 'nowrap' }}>Email / Password</Tabs.Tab>
                    <Tabs.Tab value="google">Google</Tabs.Tab>
                    <Tabs.Tab value="ldap">LDAP</Tabs.Tab>
                  </Tabs.List>
                </Tabs>

                {authMethod === "password" && (
                  <>
                    <TextInput
                      type="email"
                      label="Work email"
                      placeholder="analyst@northwind.io"
                      value={email}
                      onChange={(e) => setEmail(e.currentTarget.value)}
                      required
                    />
                    <PasswordInput
                      label="Password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.currentTarget.value)}
                      required
                    />
                  </>
                )}

                {authMethod === "ldap" && (
                  <>
                    <TextInput
                      label="Username"
                      placeholder="username"
                      value={username}
                      onChange={(e) => setUsername(e.currentTarget.value)}
                      required
                    />
                    <PasswordInput
                      label="Password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.currentTarget.value)}
                      required
                    />
                  </>
                )}

                <TextInput
                  label="Company Alias"
                  placeholder="company-alias"
                  value={companyAlias}
                  onChange={(e) => setCompanyAlias(e.currentTarget.value)}
                  required
                />

                {authMethod === "google" && (
                  <Center mt="md">

                    <Button
                      onClick={() => {
                        if (!companyAlias) {
                          setError("Please enter your Company Alias first");
                          return;
                        }

                        // Construct Google OAuth URL
                        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
                        const redirectUri = `${window.location.origin}/auth/google/callback`;
                        const scope = "email profile openid";
                        const responseType = "code";

                        // Pass companyAlias in state so we can retrieve it after callback
                        const state = btoa(JSON.stringify({ companyAlias }));

                        const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
                          `client_id=${encodeURIComponent(clientId)}&` +
                          `redirect_uri=${encodeURIComponent(redirectUri)}&` +
                          `response_type=${responseType}&` +
                          `scope=${encodeURIComponent(scope)}&` +
                          `state=${state}&` +
                          `access_type=online&` +
                          `prompt=consent`;

                        window.location.href = authUrl;
                      }}
                      variant="default"
                    >
                      Sign in with Google
                    </Button>
                  </Center>)}



                {error && (
                  <Text c="red" size="sm">{error}</Text>
                )}

                {authMethod !== "google" && (
                  <Button fullWidth mt="xl" type="submit" color="black" c="white">
                    Sign in
                  </Button>
                )}


              </Stack>
            </form>
          </Box>
        </Stack>
      </Center>
    </Container >
  );
}
